import React, { useEffect } from 'react'
import { NameDto } from '@/api/Common/Types'
import {
  RewardCategories,
  RewardsHubCreateDto,
  RewardsHubDto,
  RewardStatuses,
  TradingViewRewardType,
} from '@/api/Trading-Accounts/Types/RewardsHubDto'
import { FormField, FormSelectField } from '@/components/Form/FormField'
import { LoadingOverlay } from '@/components/LoadingOverlay/LoadingOverlay'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { validationSchema } from '@/routes/Marketing/RewardsHub/rewardsHubValidationSchema'
import { useAppDispatch, useAppSelector } from '@/store'
import { fetchRewardsCategories } from '@/store/trading-accounts/trading-accounts.actions'
import { useApiClient } from '@/utils/api/ApiClient'
import { TradingAccountsApiClient } from '@/utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from '@/utils/api/useCallbackWithForceRefresh'
import { useFetchOneWithFetchingUpdate } from '@/utils/api/useFetchOneWithFetchingUpdate'
import { WalletsApiClient } from '@/utils/api/walletsApiClient'
import { FormSubmitValues } from '@/utils/formValidation'
import { isEmpty } from '@/utils/is-empty'
import { FormikProps, withFormik } from 'formik'

interface OuterProps {
  isOpen?: boolean
  onClose(): void
  entity?: RewardsHubDto
  onSubmit(values: FormSubmitValues<RewardsHubCreateDto>): Promise<void>
}

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  return {
    fetchRewardsCategories: async () => dispatch(fetchRewardsCategories(tradingAccountsApiClient)),
  }
}

export const AddEditRewardTypeDialogUI: React.FC<FormikProps<RewardsHubCreateDto> & OuterProps> = (
  props
) => {
  const { handleSubmit, isSubmitting, setFieldValue, isValid, values } = props

  const handleSubmitForm = () => {
    handleSubmit()
  }
  const walletsApiClient = useApiClient(WalletsApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const { callback: bonusCampaignsCallback } = useCallbackWithForceRefresh(() => {
    return walletsApiClient.getEnabledBonusCampaigns()
  }, [])
  const { callback: marketingCampaignsCallback } = useCallbackWithForceRefresh(() => {
    return tradingAccountsApiClient.getEnabledMarketingCampaigns()
  }, [])

  const { data: bonusCampaigns } = useFetchOneWithFetchingUpdate(bonusCampaignsCallback)
  const { data: marketingCampaigns } = useFetchOneWithFetchingUpdate(marketingCampaignsCallback)
  const { fetchRewardsCategories } = useMapDispatchToProps()

  const rewardCategoriesResult = useAppSelector((state) => state.tradingAccounts.rewardCategories)

  useEffect(() => {
    const fetchData = async () => {
      if (isEmpty(rewardCategoriesResult)) {
        await fetchRewardsCategories()
      }
    }

    fetchData().catch(console.error)
  }, [rewardCategoriesResult, fetchRewardsCategories])

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{props.entity ? 'Edit Reward' : 'Add New Reward'}</DialogTitle>
        </DialogHeader>
        <FormField label={'Reward name (internal)'} name={'internalName'} />
        <FormField label={'Display name (client facing)'} name={'displayName'} />
        <FormSelectField
          name='rewardCategoryId'
          label='Reward Category'
          disabled={!!props.entity}
          options={
            rewardCategoriesResult?.map((reward: NameDto) => ({
              id: reward.id,
              name: reward.name ?? '',
            })) ?? []
          }
          onChange={(value) => setFieldValue('rewardCategoryId', Number(value))}
        />

        {values.rewardCategoryId === RewardCategories.SPIN && (
          <>
            <FormField
              type={'number'}
              label={'Amount of Spins'}
              name={'spinRewardConfiguration.amountOfSpins'}
            />
            <FormSelectField
              name='spinRewardConfiguration.luckyDrawCampaignId'
              label='Applicable Wheel Name'
              disabled={!!props.entity}
              options={(marketingCampaigns ?? [])?.map((marketingCampaign) => ({
                id: marketingCampaign.id,
                name: marketingCampaign.name,
              }))}
              showSearch
              onChange={(value) =>
                setFieldValue('spinRewardConfiguration.luckyDrawCampaignId', value)
              }
            />
          </>
        )}
        {values.rewardCategoryId === RewardCategories.BONUS && (
          <FormSelectField
            name='bonusRewardConfiguration.bonusCampaignId'
            label='Applicable Bonus Campaign'
            disabled={!!props.entity}
            options={(bonusCampaigns ?? [])?.map((bonusCampaign) => ({
              id: bonusCampaign.id,
              name: bonusCampaign.name,
            }))}
            showSearch
            onChange={(value) => setFieldValue('bonusRewardConfiguration.bonusCampaignId', value)}
          />
        )}
        {values.rewardCategoryId === RewardCategories.TRADING_VIEW && (
          <FormSelectField
            name='tradingViewRewardConfiguration.tradingViewPlanId'
            label='Applicable Reward'
            disabled={!!props.entity}
            options={TradingViewRewardType?.map((tradingViewType) => ({
              id: tradingViewType.id.toString(),
              name: tradingViewType.name,
            }))}
            showSearch
            onChange={(value) =>
              setFieldValue('tradingViewRewardConfiguration.tradingViewPlanId', value)
            }
          />
        )}
        <FormField
          type={'number'}
          label={'Default Expiration (days)'}
          name={'defaultExpirationInDays'}
        />

        <DialogFooter>
          <Button onClick={handleSubmitForm} disabled={isSubmitting || !isValid} type='button'>
            Save
          </Button>
        </DialogFooter>
        <LoadingOverlay isLoading={isSubmitting} />
      </DialogContent>
    </Dialog>
  )
}

export const AddEditRewardTypeDialog = withFormik<OuterProps, RewardsHubCreateDto>({
  validationSchema: validationSchema,
  enableReinitialize: true,
  validateOnMount: true,
  mapPropsToValues: ({ entity }) => {
    const categoryId = entity?.rewardCategory?.id ?? 0

    return {
      internalName: entity?.internalName ?? '',
      displayName: entity?.displayName ?? '',
      rewardCategoryId: categoryId,
      rewardStatusId: entity?.rewardStatus?.id ?? RewardStatuses.ACTIVE,
      defaultExpirationInDays: entity?.defaultExpirationInDays ?? 0,
      spinRewardConfiguration:
        categoryId === 1
          ? {
              amountOfSpins: entity?.spinRewardConfiguration?.amountOfSpins ?? 0,
              luckyDrawCampaignId: entity?.spinRewardConfiguration?.luckyDrawCampaign?.id ?? '',
            }
          : null,
      bonusRewardConfiguration:
        categoryId === 2
          ? {
              bonusCampaignId: entity?.bonusRewardConfiguration?.bonusCampaign?.id ?? '',
            }
          : null,
      tradingViewRewardConfiguration:
        categoryId === 3
          ? {
              tradingViewPlanId:
                entity?.tradingViewRewardConfiguration?.tradingViewPlan?.id?.toString() ?? '',
            }
          : null,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
})(AddEditRewardTypeDialogUI)
